import { Dialog, FormControl, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOpen } from '@/app/hooks/useOpen';
import {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { CreatePaymentDocumentPayload, DealRequest } from '@/app/api/types';
import { useForm } from 'react-hook-form';
import ModalTemplate from '@/shared/modal/modal-template';
import { UIFormLabel } from '@/shared/ui/ui-form-label';
import { useCreatePaymentsDocumentMutation } from '@/app/api/paymentsApi';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { getFetchEror } from '@/app/helpers';
import { useNavigate } from 'react-router-dom';
import { CreatePaymentFormValues } from './lib';
import FullscreenLoader from '@/shared/FullscreenLoader/FullscreenLoader';
import { CreatePaymentModalRef } from '@/features/factor-request-clarification-modal/types';
import { FormDatePicker } from '@/shared/inputs/form-date-picker';
import { FormNumericInput } from '@/shared/inputs/form-numeric-input';
import { useAppSelector } from '@/app/hooks';
import FormFormattedInput from '@/shared/inputs/form-formatted-input';

interface Props {
  id: string;
  data: DealRequest;
}

export const CreatePaymentModal = forwardRef<CreatePaymentModalRef, Props>(
  ({ id, data }, ref) => {
    const { t } = useTranslation();
    const { mfo } = useAppSelector((state) => state.auth);
    const { open, setOpen } = useOpen();
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const [paymentType, setPaymentType] = useState<'redeem' | 'payout'>(
      'payout'
    );

    const form = useForm<CreatePaymentFormValues>({
      defaultValues: new CreatePaymentFormValues(),
    });

    const { control, reset, setValue } = form;

    const [createPaymentDocument, { isLoading }] =
      useCreatePaymentsDocumentMutation();

    const onOpen = useCallback(
      (type: 'redeem' | 'payout') => {
        setPaymentType(type);
        setValue('dealId', id);
        setValue(
          'amount',
          type === 'redeem'
            ? ((data.factorPaymentAmount || 0) + (data.repaymentAmount || 0))
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
            : data.factorPaymentAmount
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        );

        // поверх и погашение factorPaymentAmount + repaymentAmount
        // внутри и погашение factorPaymentAmount + repaymentAmount

        // поверх и выплата factorPaymentAmount
        // внутри и выплата factorPaymentAmount
        setOpen(true);
      },
      [setOpen, id, setValue, data]
    );

    const onClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        reset(new CreatePaymentFormValues());
      }, 500);
    }, [setOpen, reset]);

    const getPayerAndRecipient = useMemo(() => {
      if (paymentType === 'payout') {
        return {
          payer: {
            tin: data?.factor?.tin || '',
            name: data?.factor?.name || '',
          },
          recipient: {
            tin: data?.seller?.tin || '',
            name: data?.seller?.name || '',
          },
        };
      } else {
        return {
          payer: {
            tin:
              data.factoringType === 'opened' ||
              data.factoringType === 'reverse'
                ? data?.buyer?.tin || ''
                : data?.seller?.tin || '',
            name:
              data.factoringType === 'opened' ||
              data.factoringType === 'reverse'
                ? data?.buyer?.name || ''
                : data?.seller?.name || '',
          },
          recipient: {
            tin: data?.factor?.tin || '',
            name: data?.factor?.name || '',
          },
        };
      }
    }, [paymentType, data]);

    const onSubmit = useCallback(
      async (values: CreatePaymentFormValues) => {
        const payload: CreatePaymentDocumentPayload = {
          dealId: Number(values.dealId),
          date: values.date?.format('DD/MM/YYYY') || '',
          amount: Number(values.amount.replace(/\s/g, '')),
          paymentTypeId: paymentType === 'redeem' ? 'fullRepayment' : 'payout',
          payer: {
            tin: getPayerAndRecipient.payer.tin,
            name: getPayerAndRecipient.payer.name,
            ...(paymentType === 'payout' && {
              mfo: mfo || '',
            }),
          },
          recipient: {
            tin: getPayerAndRecipient.recipient.tin,
            name: getPayerAndRecipient.recipient.name,
            ...(paymentType === 'payout' && {
              account: values.recipientAccount.replace(/\s/g, ''),
            }),
          },
        };
        try {
          await createPaymentDocument(payload).unwrap();
          navigate(`/factor/requests/${values.dealId}`);
          if (paymentType === 'redeem') {
            showNotification(t('redeem_created'), 'success');
          } else {
            showNotification(t('payment_created'), 'success');
          }
          onClose();
        } catch (error) {
          showNotification(getFetchEror(error));
        }
      },
      [
        createPaymentDocument,
        navigate,
        showNotification,
        t,
        paymentType,
        onClose,
      ]
    );

    useImperativeHandle(
      ref,
      () => ({
        open: onOpen,
        close: onClose,
      }),
      [onOpen, onClose]
    );

    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              maxWidth: 640,
              width: '100%',
            },
          }}
        >
          <ModalTemplate
            title={paymentType === 'redeem' ? t('add_redeem') : t('add_payout')}
            onClose={onClose}
            onSubmit={form.handleSubmit(onSubmit)}
            submitBtnText={t('save_and_add')}
            loading={isLoading}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <UIFormLabel
                  sx={{ fontSize: 14, fontWeight: 500, color: 'primary.main' }}
                >
                  {t('payment_type')}:
                </UIFormLabel>

                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'text.primary',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {paymentType === 'redeem'
                    ? t('payment_type_options.redeem')
                    : t('payment_type_options.payout')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <UIFormLabel
                  sx={{ fontSize: 14, fontWeight: 500, color: 'primary.main' }}
                >
                  {t('recipient')}:
                </UIFormLabel>

                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'text.primary',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getPayerAndRecipient.recipient.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <UIFormLabel
                  sx={{ fontSize: 14, fontWeight: 500, color: 'primary.main' }}
                >
                  {t('recipient_tin')}:
                </UIFormLabel>

                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'text.primary',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getPayerAndRecipient.recipient.tin}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <UIFormLabel
                  sx={{ fontSize: 14, fontWeight: 500, color: 'primary.main' }}
                >
                  {t('payer')}:
                </UIFormLabel>

                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'text.primary',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getPayerAndRecipient.payer.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <UIFormLabel
                  sx={{ fontSize: 14, fontWeight: 500, color: 'primary.main' }}
                >
                  {t('payer_tin')}:
                </UIFormLabel>

                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'text.primary',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getPayerAndRecipient.payer.tin}
                </Typography>
              </Grid>

              {paymentType === 'payout' && (
                <Grid item xs={12}>
                  <FormControl required fullWidth>
                    <UIFormLabel>{t('recipient_account')}:</UIFormLabel>
                    <FormFormattedInput
                      control={control}
                      name='recipientAccount'
                      format='#### #### #### #### ####'
                      rules={{
                        required: true,
                        validate: (value) => {
                          const cleanValue =
                            typeof value === 'string'
                              ? value.replace(/\D/g, '')
                              : '';
                          return (
                            cleanValue?.length === 20 ||
                            t('errors:required_field')
                          );
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <FormControl required fullWidth>
                  <UIFormLabel>{t('amount')}:</UIFormLabel>
                  <FormNumericInput
                    control={control}
                    name='amount'
                    decimalScale={2}
                    regExp={/^\d{1,3}(\s\d{3})*(\.\d{0,2})?$/}
                    rules={{
                      pattern: {
                        value: /^\d{1,3}(\s\d{3})*(\.\d{1,2})?$/,
                        message: t('errors:invalid_number_format'),
                      },
                    }}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl required fullWidth>
                  <UIFormLabel>{t('payment_date')}:</UIFormLabel>
                  <FormDatePicker
                    control={control}
                    name='date'
                    rules={{ required: true }}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </ModalTemplate>
        </Dialog>
        <FullscreenLoader />
      </>
    );
  }
);
